import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import {
  useLocation,
  useNavigate,
  useBeforeUnload,
  Link,
} from "react-router-dom";
import { PlusIcon, FlagIcon } from "@heroicons/react/24/outline";
import { AIActor } from "./types/actor";
import api from "./services/api";
import AIActorSelectionDialog from "./components/AIActorSelectionDialog";
import AudioSettings from "./components/AudioSettings";
import CharacterCanvasDialog from "./components/CharacterCanvasDialog";
import FlagVideoDialog from "./components/FlagVideoDialog";
import ProductDetailsFlow from "./components/ProductDetailsFlow";
import { toast } from "react-hot-toast";
import { DropdownItem } from "./components/Dropdown";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useAuth } from "./context/AuthContext";
import { transformImageUrl } from "./utils/imageUtils";
import { Toaster } from "react-hot-toast";
import UploadProgress from "./components/GeneratePage/UploadProgress";
import LoadingSpinner from "./components/GeneratePage/LoadingSpinner";
import StickyBar from "./components/GeneratePage/StickyBar";
import LoadingIcon from "./components/GeneratePage/LoadingIcon";
import BackArrow from "./components/GeneratePage/BackArrow";
import EditIcon from "./components/GeneratePage/EditIcon";
import UploadOverlay from "./components/UploadOverlay";
// Define VideoJob interface (same as in VideoJobsPage)
interface VideoJob {
  id: string;
  project_id: string;
  status: "queued" | "processing" | "completed" | "failed";
  current_step: string | null;
  progress: number;
  error_message: string | null;
  logs: Array<{
    timestamp: string;
    message: string;
  }>;
  result_video_url: string | null;
  talking_face_url: string | null;
  mux_asset_id: string | null;
  mux_playback_id: string | null;
  mux_status: string | null;
  created_at: string;
  updated_at: string;
}

interface UploadStatus {
  fileName: string;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  url?: string;
  fileId?: string;
}

interface ProjectFile {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size?: string;
  created_at: string;
}

// Interface for generated videos
interface GeneratedVideo {
  id: string;
  actor_id: string;
  video_url: string;
  video_type: string;
  job_id: string;
  created_at: string;
  mux_asset_id?: string;
  mux_playback_id?: string;
  mux_status?: string;
}

// Add this interface for our project metadata
interface ProjectMetadata {
  selectedActors?: AIActor[];
  scriptText?: string;
  videoLanguage?: string;
  languageAccent?: string;
  requirements?: string;
  productDetails?: {
    gender: string;
    ethnicity: string;
    dimensions: string;
  };
}

// Add the product_image_url to the Project interface
interface Project {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  product_image_url?: string;
  product_prompt?: string;
}

// Context menu position interface
interface ContextMenuPosition {
  x: number;
  y: number;
  jobId: string | null;
}

// Use this helper function to get the best video URL for playback
const getBestVideoUrl = (
  video:
    | GeneratedVideo
    | {
        id: string;
        video_url: string;
        isJob: boolean;
        mux_asset_id?: string | null;
        mux_playback_id?: string | null;
        mux_status?: string | null;
      }
) => {
  // If it has a Mux playback ID available, use the Mux streaming URL
  if (video.mux_playback_id && video.mux_status === "ready") {
    return `https://stream.mux.com/${video.mux_playback_id}/low.mp4`;
  }
  // Otherwise fall back to the direct URL
  return video.video_url;
};

export default function GenerateVideoPage({ itemType }: { itemType: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    projectId,
    projectName: initialProjectName,
    images,
    productPrompt,
  } = location.state || {};

  const { user, useVideoCredits, isLoading: isUserLoading } = useAuth();

  // Add project name state
  const [projectName, setProjectName] = useState(
    initialProjectName || "Untitled Project"
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const projectNameInputRef = useRef<HTMLInputElement>(null);

  const [isActorDialogOpen, setIsActorDialogOpen] = useState(false);
  const [isCharacterCanvasDialogOpen, setIsCharacterCanvasDialogOpen] =
    useState(false);
  // Store original actors when opening dialog for a single actor
  const [originalSelectedActors, setOriginalSelectedActors] = useState<
    AIActor[]
  >([]);
  const [selectedActors, setSelectedActors] = useState<AIActor[]>([]);
  const [scriptText, setScriptText] = useState("");
  const [videoLanguage, setVideoLanguage] = useState("");
  const [languageAccent, setLanguageAccent] = useState("");
  const [requirements, setRequirements] = useState("");
  const [selectedAudioSetting, setSelectedAudioSetting] =
    useState<DropdownItem | null>(null);

  // Define language and accent options

  // State for generated videos
  const [generatedVideos, setGeneratedVideos] = useState<GeneratedVideo[]>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);

  const [selectedAccent, setSelectedAccent] = useState<DropdownItem | null>(
    null
  );

  // Upload state
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);

  // Add this new state for project files
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [fileLoadError, setFileLoadError] = useState<string | null>(null);

  // Add a new state to track if we're saving metadata
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  // Add a new state to track when the script was last saved
  const [lastScriptSaved, setLastScriptSaved] = useState<Date | null>(null);
  // Add a state to track if there are unsaved script changes
  const [hasUnsavedScriptChanges, setHasUnsavedScriptChanges] = useState(false);
  // Add a state to keep track of the last saved script text for comparison
  const [lastSavedScriptText, setLastSavedScriptText] = useState("");

  const [hasLoadedMetadata, setHasLoadedMetadata] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [isProductDetailsFlowOpen, setIsProductDetailsFlowOpen] = useState(false);

  // Define the missing maxCharacters constant
  // const maxCharacters = 1800;
  const [maxCharacters, setMaxCharacters] = useState(1800);

  // Audio-related state
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [audioFiles, setAudioFiles] = useState<
    Array<{
      id: string;
      file_url: string;
      file_name: string;
      created_at: string;
      script_text: string;
    }>
  >([]);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<"actors" | "instructions">(
    "actors"
  );

  // Video Jobs state
  const [videoJobs, setVideoJobs] = useState<VideoJob[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isPollingJobs, setIsPollingJobs] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] =
    useState<ContextMenuPosition>({ x: 0, y: 0, jobId: null });

  // Confirmation dialog state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Flag dialog state
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [videoToFlag, setVideoToFlag] = useState<{
    id: string;
    url: string;
  } | null>(null);

  // State to track which actors have merged images
  const [actorsWithMergedImages, setActorsWithMergedImages] = useState<
    Record<string, boolean>
  >({});

  // State to store merged image URLs
  const [mergedImageUrls, setMergedImageUrls] = useState<
    Record<string, string>
  >({});

  const characterCount = scriptText.length;
  const isOverLimit = characterCount > maxCharacters;

  // Helper function to check if all actors have merged images
  const checkAllActorsHaveMergedImages = () => {
    if (selectedActors.length === 0) return true;

    // If this is not a product, we don't need merged images
    if (itemType !== "product") return true;

    // Check if all selected actors have merged images
    return selectedActors.every(
      (actor) => actorsWithMergedImages[actor.id] === true
    );
  };

  // Add state for the Project
  const [projectDetails, setProjectDetails] = useState<Project | null>(null);

  // Add state to track initial filters for AI actor dialog
  const [initialActorFilters, setInitialActorFilters] = useState<{
    gender: string[];
    ethnicity: string[];
    [key: string]: string[];
  } | undefined>(undefined);

  // Function to fetch generated videos for the project

  // Function to handle video playback

  // Check if an actor has a merged image
  const checkForMergedImage = async (actorId: string) => {
    if (!projectId) return;

    try {
      const response = await api.get(
        `/api/avatars/merged-image/${actorId}/${projectId}`
      );

      if (response.data && !response.data.not_found) {
        // Actor has a merged image
        setActorsWithMergedImages((prev) => ({
          ...prev,
          [actorId]: true,
        }));

        // Store the merged image URL
        if (response.data.merged_image_url) {
          setMergedImageUrls((prev) => ({
            ...prev,
            [actorId]: response.data.merged_image_url,
          }));
        }

        return true;
      } else {
        // Actor doesn't have a merged image
        setActorsWithMergedImages((prev) => ({
          ...prev,
          [actorId]: false,
        }));
        return false;
      }
    } catch (error) {
      console.error(`Error checking merged image for actor ${actorId}:`, error);
      setActorsWithMergedImages((prev) => ({
        ...prev,
        [actorId]: false,
      }));
      return false;
    }
  };

  // Fetch video jobs and generated videos when component mounts or projectId changes
  useEffect(() => {
    if (user?.is_premium === "trialing") {
      setMaxCharacters(220);
    } else {
      setMaxCharacters(2000);
    }
    if (projectId) {
      // fetchGeneratedVideos();
      fetchVideoJobs();
    }

    // Cleanup function to stop polling when component unmounts
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [projectId]);

  // Prompt user before leaving if uploads are in progress
  useBeforeUnload(
    useCallback(
      (event) => {
        if (isUploading) {
          event.preventDefault();
          return "Uploads are in progress. Are you sure you want to leave?";
        }
      },
      [isUploading]
    )
  );

  // Handle navigation away from page
  useEffect(() => {
    const handleBeforeNavigate = (e: BeforeUnloadEvent) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue =
          "Uploads are in progress. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeNavigate);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeNavigate);
    };
  }, [isUploading]);

  // Check for existing uploads when component mounts
  useEffect(() => {
    const checkExistingUploads = async () => {
      if (!projectId) return;

      try {
        // Fetch existing files for this project
        const response = await api.get(
          `/api/projects/project-files/${projectId}`
        );
        const existingFiles = response.data.files || [];

        // Store the filenames of already uploaded files
        setUploadedFiles(existingFiles.map((file: any) => file.file_name));

        console.log(`Found ${existingFiles.length} existing files for project`);
      } catch (error) {
        console.error("Error checking existing uploads:", error);
      }
    };

    checkExistingUploads();
  }, [projectId]);

  // Load project metadata before showing dialogs
  useEffect(() => {
    const loadProjectMetadata = async () => {
      if (!projectId) return;

      try {
        const response = await api.get(
          `/api/projects/project-metadata/${projectId}`
        );
        const metadata = response.data.metadata || {};
        const hasExistingData =
          !!metadata.selectedActors && metadata.selectedActors.length > 0;

        // Restore the saved data
        if (metadata.selectedActors) {
          // Fetch the full avatar data for each selected actor
          const selectedActorsData = await Promise.all(
            metadata.selectedActors.map(async (actor: any) => {
              try {
                const avatarResponse = await api.get(
                  `/api/avatars/${actor.id}`
                );
                return {
                  ...actor,
                  image: avatarResponse.data.avatar.character_image_url,
                  preview_image_url:
                    avatarResponse.data.avatar.preview_image_url,
                  niches: avatarResponse.data.avatar.tags || [],
                  voice_id: avatarResponse.data.avatar.voice_id,
                };
              } catch (error) {
                console.error(`Error fetching avatar ${actor.id}:`, error);
                return actor;
              }
            })
          );
          setSelectedActors(selectedActorsData);

          // Check for merged images for each actor
          selectedActorsData.forEach((actor) => {
            checkForMergedImage(actor.id);
          });
        }
        if (metadata.scriptText) {
          setScriptText(metadata.scriptText);
          // Initialize last saved timestamp when loading existing script text
          setLastScriptSaved(new Date());
          setHasUnsavedScriptChanges(false);
          // Keep track of the last saved script text for comparison
          setLastSavedScriptText(metadata.scriptText);
        }
        if (metadata.videoLanguage) setVideoLanguage(metadata.videoLanguage);
        if (metadata.languageAccent) setLanguageAccent(metadata.languageAccent);
        if (metadata.requirements) setRequirements(metadata.requirements);

        // Set flag that we've loaded metadata
        setHasLoadedMetadata(true);

        // If there's actor data, this isn't the first visit
        if (hasExistingData) {
          setIsFirstVisit(false);
          console.log("Existing project data found, skipping initial dialogs");
        }

        // Check if we have product details data
        const hasProductDetails = !!metadata.productDetails;
        if (itemType === "product" && isFirstVisit && !hasProductDetails) {
          // Show the product details flow for first-time product uploads
          setIsProductDetailsFlowOpen(true);
        }
      } catch (error) {
        console.error("Error loading project metadata:", error);
        setHasLoadedMetadata(true); // Still mark as loaded even on error
      }
    };

    loadProjectMetadata();
  }, [projectId]);

  // Effect to handle initial upload and dialog display
  useEffect(() => {
    if (!hasLoadedMetadata) {
      // We haven't checked for metadata yet, so don't do anything
      return;
    }

    // Check if we need to show the actor dialog
    const shouldShowActorDialog = isFirstVisit || selectedActors.length === 0;

    if (shouldShowActorDialog) {
      // Initialize upload statuses and upload files only on first visit
      if (isFirstVisit && images && images.length > 0) {
        const initialStatuses = images.map((image: File) => ({
          fileName: image.name,
          progress: 0,
          status: "pending" as const,
        }));
        setUploadStatuses(initialStatuses);

        // Start background uploads - only open AI dialog after uploads complete
        handleBackgroundUploads(images);
      } else {
        // If no images to upload or not first visit but no actors, immediately show the actor dialog
        setIsActorDialogOpen(true);
      }
    }
  }, [hasLoadedMetadata, isFirstVisit, images, projectId, projectName, selectedActors.length]);

  // Calculate overall progress whenever individual upload statuses change
  useEffect(() => {
    if (uploadStatuses.length === 0) return;

    const totalProgress = uploadStatuses.reduce(
      (sum, status) => sum + status.progress,
      0
    );
    const avgProgress = totalProgress / uploadStatuses.length;
    setOverallProgress(avgProgress);

    // Check if all uploads are complete
    const allComplete = uploadStatuses.every(
      (status) => status.status === "completed" || status.status === "error"
    );
    if (allComplete && isUploading) {
      setIsUploading(false);
      setAllUploadsComplete(true);

      // Open actor dialog after uploads complete if metadata is loaded and no actors selected
      if (hasLoadedMetadata && selectedActors.length === 0) {
        setIsActorDialogOpen(true);
      }
    }
  }, [uploadStatuses, isUploading]);

  const handleBackgroundUploads = async (images: File[]) => {
    if (!images || images.length === 0 || !projectId) return;

    console.log(`Starting background uploads for ${images.length} files...`);
    setIsUploading(true);

    // Filter out already uploaded files
    const filesToUpload = images.filter(
      (file) => !uploadedFiles.includes(file.name)
    );

    if (filesToUpload.length === 0) {
      console.log("All files already uploaded, skipping upload process");
      setAllUploadsComplete(true);
      setIsUploading(false);

      // Update statuses to show all files as completed
      setUploadStatuses(
        images.map((image) => ({
          fileName: image.name,
          progress: 100,
          status: "completed" as const,
        }))
      );

      // For product type, show product details flow for first-time uploads
      if (itemType === "product" && isFirstVisit) {
        setIsProductDetailsFlowOpen(true);
      } else {
        // For other item types, or if not first visit, show actor dialog
        setIsActorDialogOpen(true);
      }
      return;
    }

    console.log(
      `Uploading ${filesToUpload.length} new files (${
        images.length - filesToUpload.length
      } already uploaded)`
    );

    // Process each file sequentially to avoid overwhelming the server
    for (let i = 0; i < images.length; i++) {
      const file = images[i];

      // Skip already uploaded files
      if (uploadedFiles.includes(file.name)) {
        setUploadStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[i] = {
            ...newStatuses[i],
            status: "completed",
            progress: 100,
          };
          return newStatuses;
        });
        continue;
      }

      try {
        // Update status to uploading
        setUploadStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[i] = {
            ...newStatuses[i],
            status: "uploading",
            progress: 10,
          };
          return newStatuses;
        });

        // Upload through our backend
        console.log(`Uploading ${file.name} through backend...`);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("project_id", projectId);

        // Update progress
        setUploadStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[i] = { ...newStatuses[i], progress: 30 };
          return newStatuses;
        });

        // Upload to our backend
        const uploadResponse = await api.post(
          "/api/files/upload-file",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            // Track upload progress
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 100)
              );
              setUploadStatuses((prev) => {
                const newStatuses = [...prev];
                newStatuses[i] = {
                  ...newStatuses[i],
                  progress: 30 + percentCompleted * 0.6, // Scale to 30-90%
                };
                return newStatuses;
              });
            },
          }
        );

        if (!uploadResponse.data || !uploadResponse.data.file_url) {
          throw new Error("Upload failed - no file URL returned");
        }

        // Get the file URL and ID from the response
        let { file_url, file_id } = uploadResponse.data;

        // Ensure the file URL starts with the correct public URL base
        if (!file_url.startsWith("https://img.ugz.ai")) {
          // Extract the path part after the domain
          const urlParts = file_url.split("/");
          const pathParts = urlParts.slice(3); // Skip protocol and domain parts

          // Construct the new URL with the correct base
          file_url = `https://img.ugz.ai/${pathParts.join("/")}`;
        }

        console.log(`Successfully uploaded ${file.name} to R2 via backend`);

        // Add to uploaded files list to prevent re-upload
        setUploadedFiles((prev) => [...prev, file.name]);

        // Update status to completed
        setUploadStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[i] = {
            ...newStatuses[i],
            status: "completed",
            progress: 100,
            url: file_url,
            fileId: file_id,
          };
          return newStatuses;
        });

        console.log(`Recorded upload completion for ${file.name}`);
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);

        // Update status to error
        setUploadStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[i] = { ...newStatuses[i], status: "error" };
          return newStatuses;
        });
      }
    }

    // Check if all uploads are complete
    setAllUploadsComplete(true);
    setIsUploading(false);
    console.log("All background uploads completed");

    // First check if we have metadata loaded
    if (hasLoadedMetadata) {
      // For product type, show product details flow for first-time uploads
      if (itemType === "product" && isFirstVisit) {
        setIsProductDetailsFlowOpen(true);
      } else {
        // For other item types, or if not first visit, show actor dialog
        setIsActorDialogOpen(true);
      }
    }
  };

  // Modify handleSelectActors to show the character canvas dialog only for actors without masks
  const handleSelectActors = async (actors: AIActor[]) => {
    setIsActorDialogOpen(false);

    // Check if the actor selection has changed
    const selectedIds = selectedActors.map((actor) => actor.id).sort();
    const newIds = actors.map((actor) => actor.id).sort();
    const hasChanged =
      selectedIds.length !== newIds.length ||
      selectedIds.some((id, index) => id !== newIds[index]);

    if (hasChanged) {
      // Ensure each actor has the required properties (x, y, width, height)
      const enhancedActors = actors.map((actor) => ({
        ...actor,
        x: actor.x ?? 0,
        y: actor.y ?? 0,
        width: actor.width ?? 0,
        height: actor.height ?? 0,
      }));

      // Set the selected actors (temporarily store all actors)
      setSelectedActors(enhancedActors);

      // Check for merged images for each actor
      const mergedStatusPromises = enhancedActors.map((actor) =>
        checkForMergedImage(actor.id)
      );
      const mergedResults = await Promise.all(mergedStatusPromises);

      // Create array of actors that need merged images (with red dots)
      const actorsNeedingMergedImages = enhancedActors.filter(
        (actor, index) => !mergedResults[index] && itemType === "product"
      );

      // If we already have a projectId, save the metadata
      if (projectId) {
        await saveProjectMetadata({ selectedActors: enhancedActors });
      }

      // Show the character canvas dialog only if there are actors with no merged images
      if (actorsNeedingMergedImages.length > 0 && itemType === "product") {
        // Store original actors before modifying
        setOriginalSelectedActors([...enhancedActors]);
        // Only open dialog for actors without merged images (red dots)
        setSelectedActors(actorsNeedingMergedImages);
        setIsCharacterCanvasDialogOpen(true);
      }
    }
  };

  // Add a handler for when character canvas dialog closes
  const handleCharacterCanvasDialogClose = () => {
    setIsCharacterCanvasDialogOpen(false);
    // Restore the original selectedActors from our stored copy
    if (originalSelectedActors.length > 0) {
      setSelectedActors(originalSelectedActors);

      // Re-check merged image status for all actors to refresh indicators and URLs
      originalSelectedActors.forEach((actor) => {
        checkForMergedImage(actor.id);
      });

      // If all actors now have merged images, show a success toast
      if (
        originalSelectedActors.every(
          (actor) => actorsWithMergedImages[actor.id] === true
        )
      ) {
        toast.success(
          "All product images have been applied. You can now generate your videos!"
        );
      }
    }
  };

  // Add this new function to save project metadata
  const saveProjectMetadata = async (metadata: Partial<ProjectMetadata>) => {
    if (!projectId) return;

    try {
      setIsSavingMetadata(true);
      await api.post(`/api/projects/project-metadata/${projectId}`, {
        metadata,
      });
      console.log("Saved project metadata", metadata);

      // Update the last saved timestamp if this save included script text
      if (metadata.scriptText !== undefined) {
        setLastScriptSaved(new Date());
        setHasUnsavedScriptChanges(false);
        // Update the last saved script text
        setLastSavedScriptText(metadata.scriptText);
      }
    } catch (error) {
      console.error("Error saving project metadata:", error);
    } finally {
      setIsSavingMetadata(false);
    }
  };

  // Modify removeActor to also update the saved metadata
  const removeActor = async (actorId: string) => {
    const updatedActors = selectedActors.filter(
      (actor) => actor.id !== actorId
    );
    setSelectedActors(updatedActors);

    // Remove actor from merged images tracking
    setActorsWithMergedImages((prev) => {
      const newState = { ...prev };
      delete newState[actorId];
      return newState;
    });

    // Remove actor from merged image URLs
    setMergedImageUrls((prev) => {
      const newState = { ...prev };
      delete newState[actorId];
      return newState;
    });

    // Save the updated actor list
    await saveProjectMetadata({
      selectedActors: updatedActors,
    });
  };

  // Enhanced debounced save for script text changes
  useEffect(() => {
    // Skip if project not loaded or metadata hasn't been loaded yet
    if (!projectId || !hasLoadedMetadata) return;

    // Only mark changes as unsaved when the script text actually differs from the last saved text
    // Special case: For empty scripts, only mark as unsaved if user has typed something
    const textHasChanged =
      lastSavedScriptText !== ""
        ? scriptText !== lastSavedScriptText
        : scriptText !== "";

    // Check if text is over the character limit
    const isOverLimit = scriptText.length > maxCharacters;

    // Only mark as unsaved if within character limit
    setHasUnsavedScriptChanges(textHasChanged && !isOverLimit);

    // Only save if text has actually changed and is not over the limit
    if (textHasChanged && !isOverLimit) {
      // Create debounced save with 2 second delay
      const timer = setTimeout(() => {
        saveProjectMetadata({ scriptText });
      }, 2000); // 2 seconds

      return () => clearTimeout(timer);
    }
  }, [
    scriptText,
    projectId,
    lastSavedScriptText,
    hasLoadedMetadata,
    maxCharacters,
  ]);

  // Add handlers to save other fields when they change
  const handleLanguageChange = (item: DropdownItem | null) => {
    setSelectedAudioSetting(item);
    if (item) {
      saveProjectMetadata({ videoLanguage: item.value });
    }
  };

  const handleAccentChange = (item: DropdownItem | null) => {
    setSelectedAccent(item);
    if (item) {
      saveProjectMetadata({ languageAccent: item.value });
    }
  };

  // Add this effect to fetch project files when the component mounts or projectId changes
  useEffect(() => {
    const fetchProjectFiles = async () => {
      if (!projectId) return;

      setIsLoadingFiles(true);
      setFileLoadError(null);

      try {
        const response = await api.get(
          `/api/projects/project-files/${projectId}`
        );
        const files = response.data.files || [];

        // Filter to only include image files
        const imageFiles = files.filter((file: ProjectFile) =>
          file.file_type.startsWith("image/")
        );

        setProjectFiles(imageFiles);
        console.log(`Loaded ${imageFiles.length} image files for project`);
      } catch (error) {
        console.error("Error fetching project files:", error);
        setFileLoadError("Failed to load project images");
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchProjectFiles();
  }, [projectId]);

  // Add an effect to fetch audio files when the component mounts
  useEffect(() => {
    // Only fetch audio if we have a project ID and metadata is loaded
    if (projectId && hasLoadedMetadata) {
      fetchProjectAudio();
    }

    // Cleanup function to stop audio playback when component unmounts
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
      }
    };
  }, [projectId, hasLoadedMetadata]);

  // Add a manual save function
  const handleManualSave = () => {
    // Only allow saving if text is not over the limit
    if (
      projectId &&
      scriptText !== lastSavedScriptText &&
      scriptText.length <= maxCharacters
    ) {
      saveProjectMetadata({ scriptText });
    }
  };

  // Function to fetch existing audio files for the project
  const fetchProjectAudio = async () => {
    if (!projectId) return;

    setIsLoadingAudio(true);
    try {
      const response = await api.get(`/api/audio/project-audio/${projectId}`);
      if (response.data && response.data.audio_files) {
        setAudioFiles(response.data.audio_files);
      }
    } catch (error) {
      console.error("Error fetching project audio:", error);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Function to handle audio generation - first time generation
  const handleGenerateAudio = async () => {
    if (!projectId || !scriptText || isOverLimit) return;

    setIsGeneratingAudio(true);
    try {
      // Generate audio from the current script text
      const response = await api.post(
        "/api/audio/generate-audio",
        {
          text: scriptText,
          voice_id: "21m00Tcm4TlvDq8ikWAM", // Default voice
          model_id: "eleven_monolingual_v1", // Default model
        },
        {
          params: { project_id: projectId },
        }
      );

      if (response.data) {
        // Directly update our audio files with the result
        setAudioFiles([response.data]);
        console.log("Audio generated successfully:", response.data);
        toast.success("Audio generated successfully!");
      }
    } catch (error) {
      console.error("Error generating audio:", error);
      toast.error("There was an error generating the audio. Please try again.");
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Function to handle the "Next" button click - Shows the character canvas dialog for actors without merged images
  const handleNextForMergedImages = () => {
    if (selectedActors.length === 0) return;

    // Filter to get only actors that don't have merged images
    const actorsNeedingMergedImages = selectedActors.filter(
      (actor) => actorsWithMergedImages[actor.id] !== true
    );

    if (actorsNeedingMergedImages.length > 0) {
      // Store original actors before modifying
      setOriginalSelectedActors([...selectedActors]);

      // Set selectedActors to only those needing merged images
      setSelectedActors(actorsNeedingMergedImages);

      // Open the character canvas dialog
      setIsCharacterCanvasDialogOpen(true);
    } else {
      // If all actors have merged images already, just proceed to generate
      handleGenerateVideo();
    }
  };

  const handleGenerateVideo = async () => {
    // For product type, first check if all actors have merged images
    if (itemType === "product" && !checkAllActorsHaveMergedImages()) {
      // Trigger the next flow instead
      handleNextForMergedImages();
      return;
    }

    // Ensure user data is loaded
    if (isUserLoading) {
      toast.error("User data is still loading. Please try again in a moment.");
      return;
    }

    // Check if user is logged in
    if (!user) {
      toast.error("You need to be logged in to generate a video.");
      return;
    }

    // Check if there's enough available credits for selected actors
    if (user.videos_left < selectedActors.length) {
      toast.error(
        `Not enough video credits. You need ${selectedActors.length} credits but have ${user.videos_left}.`
      );
      return;
    }

    // Check if script is empty
    if (!scriptText.trim()) {
      toast.error("Please enter a script before generating a video.");
      return;
    }

    // Check if script is within character limit
    if (isOverLimit) {
      toast.error(
        `Your script exceeds the ${maxCharacters} character limit. Please shorten it, or purchase a premium plan before generating.`
      );
      return;
    }

    // Set processing state
    setIsProcessing(true);

    try {
      // Use the credits for all actors at once
      // Create a video job for each actor
      const jobPromises = selectedActors.map(async (actor) => {
        try {
          // Create a job specifically for this actor
          const response = await api.post("/api/videos/generate", {
            project_id: projectId,
            actor_id: actor.id, // Send the specific actor ID for this job
          });

          return response.data;
        } catch (error) {
          console.error(`Error creating job for actor ${actor.name}:`, error);
          return null;
        }
      });

      // Wait for all job creation requests to complete
      const jobResults = await Promise.all(jobPromises);

      // Filter out any failed job creations
      const successfulJobs = jobResults.filter((job) => job !== null);

      if (successfulJobs.length > 0) {
        toast.success(
          `Successfully created ${successfulJobs.length} video jobs`
        );

        // Fetch the video jobs immediately to show them in the UI
        fetchVideoJobs();

        // Start polling for updates
        startPollingJobs();
      } else {
        toast.error("Failed to create any video jobs");
      }
    } catch (error) {
      console.error("Error generating video:", error);
      toast.error("Error generating video. Please try again.");
    } finally {
      // Scroll to the top of the page
      window.scrollTo(0, 0);

      setIsProcessing(false);
    }
  };

  // Add this useEffect after other useEffects to track sidebar width
  useEffect(() => {
    const updateSidebarWidth = () => {
      const sidebar = document.querySelector(
        'div[class*="w-16"], div[class*="w-64"]'
      );
      if (sidebar) {
        const sidebarWidth = sidebar.getBoundingClientRect().width;
        document.documentElement.style.setProperty(
          "--sidebar-width",
          `${sidebarWidth}px`
        );
      }
    };

    // Initial update
    updateSidebarWidth();

    // Add resize listener
    window.addEventListener("resize", updateSidebarWidth);

    // Monitor sidebar changes
    const observer = new MutationObserver(updateSidebarWidth);
    const sidebar = document.querySelector(
      'div[class*="w-16"], div[class*="w-64"]'
    );
    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => {
      window.removeEventListener("resize", updateSidebarWidth);
      observer.disconnect();
    };
  }, []);

  // Function to update project name
  const updateProjectName = async (newName: string) => {
    if (!projectId || newName.trim() === "") return;

    // If name is unchanged, just exit edit mode without an API call
    if (newName.trim() === projectName) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);

    try {
      // Send request to rename the project
      const response = await api.post("/api/projects/rename-project", {
        project_id: projectId,
        name: newName.trim(),
      });

      // Update the project name in the UI
      setProjectName(response.data.name);
      toast.success("Project name updated successfully");
    } catch (error: any) {
      console.error("Error updating project name:", error);

      toast.error(
        `Failed to update project name: ${
          error.response?.data?.detail || "Unknown error"
        }`
      );

      // Reset to previous name in case of error
      if (projectNameInputRef.current) {
        projectNameInputRef.current.value = projectName;
      }
    } finally {
      setIsSavingName(false);
      setIsEditingName(false);
    }
  };

  // Handle name edit submission
  const handleProjectNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newName = projectNameInputRef.current?.value || "";
    updateProjectName(newName);
  };

  // Handle blur event on project name input
  const handleProjectNameBlur = () => {
    const newName = projectNameInputRef.current?.value || "";
    updateProjectName(newName);
  };

  // Handle escape key to cancel editing
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsEditingName(false);
      if (projectNameInputRef.current) {
        projectNameInputRef.current.value = projectName;
      }
    }
  };

  // Fetch project details if project name isn't passed in location state
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId || initialProjectName) return;

      try {
        const response = await api.get(`/api/projects/${projectId}`);
        if (response.data) {
          setProjectName(response.data.name);
          setProjectDetails(response.data);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };

    fetchProjectDetails();
  }, [projectId, initialProjectName]);

  // Auto-focus on project name input when editing starts
  useEffect(() => {
    if (isEditingName && projectNameInputRef.current) {
      projectNameInputRef.current.focus();
      projectNameInputRef.current.select();
    }
  }, [isEditingName]);

  // Update firstProductImage to prefer the background-removed image when available
  const firstProductImage = projectDetails?.product_image_url
    ? {
        id: "bg-removed",
        file_name: "bg-removed.png",
        file_url: projectDetails.product_image_url,
        file_type: "image/png",
        created_at: new Date().toISOString(),
      }
    : projectFiles.length > 0
    ? projectFiles[0]
    : null;

  // Function to fetch video jobs for the project
  const fetchVideoJobs = async () => {
    if (!projectId) return;

    setIsLoadingJobs(true);
    try {
      const response = await api.get(
        `/api/videos/jobs?project_id=${projectId}`
      );
      if (response.data) {
        setVideoJobs(response.data);

        // Check if there are any jobs that need monitoring (queued or processing)
        const jobsToMonitor = response.data.some(
          (job: VideoJob) =>
            job.status === "processing" || job.status === "queued"
        );

        // If there are jobs to monitor and we're not already polling, start polling
        if (jobsToMonitor && !isPollingJobs) {
          startPollingJobs();
        } else if (!jobsToMonitor && isPollingJobs) {
          // If no jobs to monitor and we are polling, stop polling
          stopPollingJobs();
        }
      }
    } catch (error) {
      console.error("Error fetching video jobs:", error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  // Start polling for job updates
  const startPollingJobs = () => {
    // Stop any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log("Starting polling for job updates");

    // Start a new polling interval
    const intervalId = setInterval(fetchVideoJobs, 3000); // Poll every 3 seconds

    pollingIntervalRef.current = intervalId;
    setIsPollingJobs(true);
  };

  // Stop polling for job updates
  const stopPollingJobs = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPollingJobs(false);
  };

  // Function to handle right-click on a job card
  const handleContextMenu = (e: React.MouseEvent, jobId: string) => {
    e.preventDefault(); // Prevent default browser context menu

    // Set context menu position and job id
    setContextMenuPosition({
      x: e.clientX,
      y: e.clientY,
      jobId: jobId,
    });

    setShowContextMenu(true);
  };

  // Function to handle clicking outside the context menu to close it
  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(false);
    };

    if (showContextMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showContextMenu]);

  // Function to delete a video job
  const deleteVideoJob = async (jobId: string) => {
    if (!jobId) return;

    setIsDeleting(true);

    try {
      const response = await api.delete(`/api/videos/jobs/${jobId}`);

      if (response.status === 200) {
        // Remove the job from the state
        setVideoJobs(videoJobs.filter((job) => job.id !== jobId));
        toast.success("Video job deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting video job:", error);
      toast.error("Failed to delete video job");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
      setJobToDelete(null);
    }
  };

  // Function to handle opening the flag dialog
  const handleOpenFlagDialog = (jobId: string, videoUrl: string) => {
    setVideoToFlag({ id: jobId, url: videoUrl });
    setShowFlagDialog(true);
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#333",
            color: "#fff",
          },
          success: {
            style: {
              background: "green",
            },
          },
          error: {
            style: {
              background: "red",
            },
          },
        }}
      />
      {/* Full-page upload overlay */}
      <UploadOverlay isVisible={isUploading} progress={overallProgress} />
      {/* Product Details Flow - Only show after metadata is loaded */}
      {hasLoadedMetadata && (
        <ProductDetailsFlow
          isOpen={isProductDetailsFlowOpen}
          onClose={(selectedDetails) => {
            setIsProductDetailsFlowOpen(false);

            // Save the selected details in state for passing to AIActorSelectionDialog
            if (selectedDetails) {
              // Update project metadata with the product details
              saveProjectMetadata({ productDetails: selectedDetails });

              // Set the initial filters for the AI actor dialog
              // Using direct values that match database values
              setInitialActorFilters({
                gender: selectedDetails.gender !== "any" ? [selectedDetails.gender] : [],
                ethnicity: selectedDetails.ethnicity !== "any" ? [selectedDetails.ethnicity] : []
              });

              console.log("Setting initial filters:", {
                gender: selectedDetails.gender !== "any" ? [selectedDetails.gender] : [],
                ethnicity: selectedDetails.ethnicity !== "any" ? [selectedDetails.ethnicity] : []
              });
            }

            // Automatically open the AI actor dialog after product details flow completes
            setIsActorDialogOpen(true);
          }}
          projectId={projectId}
          productPrompt={projectDetails?.product_prompt}
        />
      )}
      <AIActorSelectionDialog
        isOpen={isActorDialogOpen}
        onSelectActors={handleSelectActors}
        onClose={() => setIsActorDialogOpen(false)}
        currentSelectedActors={selectedActors}
        itemType={itemType}
        projectId={projectId}
        productPrompt={projectDetails?.product_prompt}
        // Pass initial filter values - use our new state for this
        initialFilters={initialActorFilters}
        // productImage={projectDetails?.product_image_url ?? ""}
      />
      <CharacterCanvasDialog
        isOpen={isCharacterCanvasDialogOpen}
        productPrompt={projectDetails?.product_prompt}
        onClose={handleCharacterCanvasDialogClose}
        selectedActors={selectedActors}
        productImage={firstProductImage}
        projectId={projectId}
        onMaskGenerated={checkForMergedImage}
      />
      {/* Flag Video Dialog */}
      {videoToFlag && (
        <FlagVideoDialog
          isOpen={showFlagDialog}
          onClose={() => {
            setShowFlagDialog(false);
            setVideoToFlag(null);
          }}
          projectId={projectId}
          videoId={videoToFlag.id}
          videoUrl={videoToFlag.url}
        />
      )}

      {/* Only show the main UI once metadata is loaded (prevents flashing) */}
      {hasLoadedMetadata && (
        <div className="container mx-auto p-4 pb-24">
          {/* Project Name Header */}
          <div className="border-gray-200 dark:border-gray-800 py-3 mb-6 bg-white dark:bg-gray-900 transition-colors duration-200 px-4 rounded-xl border border-b-3">
            <div className="flex items-center justify-between">
              {/* Back button */}
              <button
                onClick={() => window.history.back()}
                className="flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 hover:cursor-pointer"
              >
                <BackArrow />
                Back
              </button>

              {/* Project name (centered) */}
              <div className="flex-1 flex justify-center text-center">
                {isEditingName ? (
                  <form
                    onSubmit={handleProjectNameSubmit}
                    className="flex items-center justify-center"
                  >
                    <input
                      ref={projectNameInputRef}
                      type="text"
                      defaultValue={projectName}
                      onBlur={handleProjectNameBlur}
                      onKeyDown={handleKeyDown}
                      className="text-2xl font-semibold bg-transparent border-b-2 border-blue-500 dark:border-blue-400 focus:outline-none px-1 py-1 text-gray-900 dark:text-white transition-colors duration-200 text-center"
                      placeholder="Enter project name"
                      autoFocus
                    />
                    {isSavingName && (
                      <div className="ml-2 text-blue-500 dark:text-blue-400">
                        <LoadingIcon />
                      </div>
                    )}
                  </form>
                ) : (
                  <div className="flex items-center justify-center group">
                    <h1
                      className="text-2xl font-semibold text-gray-900 dark:text-white cursor-pointer transition-colors duration-200 hover:text-blue-600 dark:hover:text-blue-400 text-center"
                      onClick={() => setIsEditingName(true)}
                    >
                      {projectName}
                      <span className="ml-2 text-blue-500 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <EditIcon />
                      </span>
                    </h1>
                    <div className="ml-2 text-sm text-gray-500 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      Click to edit
                    </div>
                  </div>
                )}
              </div>

              {/* Credits indicator */}
              <div className="flex items-center justify-center">
                <Link
                  to="/pricing"
                  className="border border-gray-300 px-3 py-1 flex flex-row items-center rounded-md hover:cursor-pointer relative pb-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="h-4 w-4 fill-yellow-500 mr-1"
                  >
                    {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                    <path d="M512 80c0 18-14.3 34.6-38.4 48c-29.1 16.1-72.5 27.5-122.3 30.9c-3.7-1.8-7.4-3.5-11.3-5C300.6 137.4 248.2 128 192 128c-8.3 0-16.4 .2-24.5 .6l-1.1-.6C142.3 114.6 128 98 128 80c0-44.2 86-80 192-80S512 35.8 512 80zM160.7 161.1c10.2-.7 20.7-1.1 31.3-1.1c62.2 0 117.4 12.3 152.5 31.4C369.3 204.9 384 221.7 384 240c0 4-.7 7.9-2.1 11.7c-4.6 13.2-17 25.3-35 35.5c0 0 0 0 0 0c-.1 .1-.3 .1-.4 .2c0 0 0 0 0 0s0 0 0 0c-.3 .2-.6 .3-.9 .5c-35 19.4-90.8 32-153.6 32c-59.6 0-112.9-11.3-148.2-29.1c-1.9-.9-3.7-1.9-5.5-2.9C14.3 274.6 0 258 0 240c0-34.8 53.4-64.5 128-75.4c10.5-1.5 21.4-2.7 32.7-3.5zM416 240c0-21.9-10.6-39.9-24.1-53.4c28.3-4.4 54.2-11.4 76.2-20.5c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 19.3-16.5 37.1-43.8 50.9c-14.6 7.4-32.4 13.7-52.4 18.5c.1-1.8 .2-3.5 .2-5.3zm-32 96c0 18-14.3 34.6-38.4 48c-1.8 1-3.6 1.9-5.5 2.9C304.9 404.7 251.6 416 192 416c-62.8 0-118.6-12.6-153.6-32C14.3 370.6 0 354 0 336l0-35.4c12.5 10.3 27.6 18.7 43.9 25.5C83.4 342.6 135.8 352 192 352s108.6-9.4 148.1-25.9c7.8-3.2 15.3-6.9 22.4-10.9c6.1-3.4 11.8-7.2 17.2-11.2c1.5-1.1 2.9-2.3 4.3-3.4l0 3.4 0 5.7 0 26.3zm32 0l0-32 0-25.9c19-4.2 36.5-9.5 52.1-16c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 10.5-5 21-14.9 30.9c-16.3 16.3-45 29.7-81.3 38.4c.1-1.7 .2-3.5 .2-5.3zM192 448c56.2 0 108.6-9.4 148.1-25.9c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 44.2-86 80-192 80S0 476.2 0 432l0-35.4c12.5 10.3 27.6 18.7 43.9 25.5C83.4 438.6 135.8 448 192 448z" />
                  </svg>
                  <span>{user?.videos_left} credits left</span>
                  <span className="ml-2 bg-gray-200 rounded-md bg-gradient-to-b from-purple-700 to-purple-400 border-2 border-b-3 border-purple-800 fill-white hover:cursor-pointer p-0.5">
                    <PlusIcon />
                  </span>
                  {/* Credit progress bar */}
                  <div className="absolute bottom-0 left-0 w-full h-1 overflow-hidden rounded-b-md">
                    <div
                      className="h-full bg-blue-500 shadow-[0_0_5px_2px_rgba(59,130,246,0.5)] transition-all duration-300 ease-in-out"
                      style={{ width: `${(17 / 20) * 100}%` }}
                    ></div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* Optional: Add an upload status indicator */}
          {uploadStatuses.length > 0 && !allUploadsComplete && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Uploading files in background...
              </h3>
              <div className="mt-2 space-y-2">
                {uploadStatuses.map((status, index) => (
                  <div key={index} className="flex items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-40 truncate">
                      {status.fileName}
                    </span>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 ml-2">
                      <div
                        className={`h-2.5 rounded-full ${
                          status.status === "error"
                            ? "bg-red-500"
                            : status.status === "completed"
                            ? "bg-green-500"
                            : "bg-blue-500"
                        }`}
                        style={{ width: `${status.progress}%` }}
                      ></div>
                    </div>
                    <span className="ml-2 text-xs">
                      {status.status === "error"
                        ? "Error"
                        : status.status === "completed"
                        ? "Done"
                        : status.status === "uploading"
                        ? "Uploading"
                        : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video Jobs & Generated Videos Section - Combined */}
          {videoJobs.filter(
            (job) =>
              job.status === "completed" ||
              job.status === "processing" ||
              job.status === "queued" ||
              job.status === "failed"
          ).length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                Generated Videos
                {(isLoadingJobs || isLoadingVideos) && (
                  <span className="ml-3 text-sm font-normal text-blue-500 dark:text-blue-400 flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </span>
                )}
              </h2>

              {/* Combined display of jobs and videos */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* First display video jobs in progress */}
                {videoJobs
                  .filter((job) => job.status !== "completed")
                  .map((job) => (
                    <div
                      key={job.id}
                      className={`rounded-lg overflow-hidden transition-all hover:shadow-lg ${
                        job.status === "processing" || job.status === "queued"
                          ? "bg-blue-100 dark:bg-blue-800/40 relative border-2 border-blue-600 dark:border-blue-500 shadow-[0_0_12px_rgba(37,99,235,0.5)] dark:shadow-[0_0_12px_rgba(59,130,246,0.4)] animate-pulse"
                          : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md"
                      }`}
                      onContextMenu={(e) => handleContextMenu(e, job.id)}
                    >
                      <div className="relative aspect-[9/16] p-4">
                        {/* Show progress information for jobs that are queued or processing */}
                        <div className="flex flex-col justify-center h-full">
                          {job.status === "processing" && (
                            <div className="mt-3">
                              <div className="p-4 rounded-lg flex flex-col items-center">
                                <img src="./logo.svg" className="w-16 h-16" />
                                <div className="text-gray-800 dark:text-gray-100 font-medium mb-1.5 text-center">
                                  Generating...
                                </div>
                                <div className="w-25 bg-blue-500 dark:bg-blue-600/30 rounded-full h-2.5 mb-1.5">
                                  <div
                                    className="400 h-2.5 rounded-full transition-all duration-300 bg-blue-400"
                                    style={{ width: `${job.progress}%` }}
                                  ></div>
                                </div>
                                {/* <div className="text-xs text-center mt-1 text-blue-700 dark:text-blue-200">
                                  {job.progress}%
                                </div> */}

                                {/* Calculate time remaining based on 25 min total time */}
                                <div className="text-xs text-center text-gray-700 dark:text-blue-200">
                                  {(() => {
                                    // Parse the created_at timestamp
                                    const createdTime = new Date(
                                      job.created_at
                                    );
                                    const now = new Date();

                                    // Since the DB has future dates (2025), we can't rely on elapsed time
                                    // Instead, use the progress percentage to estimate remaining time

                                    // Total expected time is 18 minutes (in ms)
                                    const totalTimeMs = 18 * 60 * 1000;

                                    // Use progress to calculate estimated remaining time
                                    // If progress is 0, use default time
                                    if (job.progress === 0) {
                                      return "(About 18 minutes left)";
                                    }

                                    // Calculate remaining time based on progress percentage
                                    const remainingMs =
                                      totalTimeMs * (1 - job.progress / 100);

                                    // Convert to minutes and seconds
                                    const minutes = Math.floor(
                                      remainingMs / (60 * 1000)
                                    );
                                    const seconds = Math.floor(
                                      (remainingMs % (60 * 1000)) / 1000
                                    );

                                    if (minutes > 0) {
                                      return `(${minutes} minute${
                                        minutes !== 1 ? "s" : ""
                                      } and ${seconds} second${
                                        seconds !== 1 ? "s" : ""
                                      } left)`;
                                    } else {
                                      return `(${seconds} second${
                                        seconds !== 1 ? "s" : ""
                                      } left)`;
                                    }
                                  })()}
                                </div>
                              </div>
                            </div>
                          )}
                          {job.status === "queued" && (
                            <div className="mt-3">
                              <div className="p-4 rounded-lg flex flex-col items-center">
                                <img src="./logo.svg" className="w-16 h-16" />
                                <div className="text-gray-800 dark:text-gray-100 font-medium mb-1.5 text-center">
                                  Queued...
                                </div>
                                <div className="text-xs text-center text-gray-700 dark:text-blue-200">
                                  (Waiting to start)
                                </div>
                              </div>
                            </div>
                          )}
                          {job.status === "failed" && (
                            <div className="mt-3">
                              <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded text-red-700 dark:text-red-400 text-sm truncate">
                                {job.error_message || "Unknown error occurred"}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                {/* Then display completed jobs with videos and generated videos */}
                {videoJobs
                  .filter((job) => job.status === "completed")
                  .map((item) => (
                    <div
                      key={item.id}
                      className="dark:bg-gray-800 shadow-md transition-all hover:shadow-lg border border-gray-200 dark:border-gray-700 relative rounded-md"
                      onContextMenu={(e) => handleContextMenu(e, item.id)}
                    >
                      <div className="aspect-[9/16] bg-gray-100 dark:bg-gray-900 rounded-md">
                        <video
                          controls
                          src={
                            item.talking_face_url ? item.talking_face_url : ""
                          }
                          className="w-full h-full rounded-md"
                        />
                      </div>
                      <div className="absolute top-2 right-2 flex space-x-2">
                        <button
                          className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-md hover:bg-white dark:hover:bg-gray-700 transition-colors hover:cursor-pointer"
                          onClick={() =>
                            handleOpenFlagDialog(
                              item.id,
                              item.talking_face_url || ""
                            )
                          }
                          title="Report issue with this video"
                        >
                          <FlagIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
                        </button>
                        <button
                          className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-md hover:bg-white dark:hover:bg-gray-700 transition-colors hover:cursor-pointer"
                          onClick={() =>
                            window.open(item.talking_face_url || "", "_blank")
                          }
                          title="Download video"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-gray-700 dark:text-gray-200"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
              </div>

              {videoJobs.length === 0 &&
                generatedVideos.length === 0 &&
                !isLoadingJobs &&
                !isLoadingVideos && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg text-center">
                    <p className="text-gray-600 dark:text-gray-400">
                      No videos generated yet. Select actors and generate a
                      video to get started.
                    </p>
                  </div>
                )}
            </div>
          )}

          {/* Context Menu */}
          {showContextMenu && (
            <div
              className="fixed bg-white dark:bg-gray-800 rounded-md shadow-lg p-1 z-50 border border-gray-200 dark:border-gray-700"
              style={{
                top: `${contextMenuPosition.y}px`,
                left: `${contextMenuPosition.x}px`,
              }}
            >
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowContextMenu(false);
                  setJobToDelete(contextMenuPosition.jobId);
                  setShowDeleteConfirmation(true);
                }}
              >
                Delete Job
              </button>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirmation && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                  Delete Video Job
                </h3>
                <p className="mb-6 text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete this video job? This action
                  cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setJobToDelete(null);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => jobToDelete && deleteVideoJob(jobToDelete)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <span className="flex items-center">
                        <LoadingIcon />
                        Deleting...
                      </span>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 bg-white dark:bg-gray-900 rounded-lg p-6">
            <div className="">
              <h1 className="text-2xl font-semibold tracking-normal text-gray-900 dark:text-white">
                Video Script
              </h1>
              <div className="flex justify-between mb-2">
                <h3 className="text-md text-gray-500 dark:text-gray-400">
                  What do you want your avatars to say?
                </h3>
                <p
                  className={`text-sm ${
                    isOverLimit
                      ? "text-red-500 font-bold"
                      : "text-purple-500 dark:text-purple-400"
                  }`}
                >
                  {scriptText.length}/{maxCharacters}
                </p>
              </div>
              <textarea
                value={scriptText}
                onChange={(e) => setScriptText(e.target.value)}
                className={`w-full h-32 p-3 border-2 ${
                  isOverLimit
                    ? "border-red-300 dark:border-red-600 focus:ring-red-500/50 dark:focus:ring-red-500/30"
                    : "border-purple-300 dark:border-purple-700 focus:ring-purple-500/50 dark:focus:ring-purple-500/30"
                } rounded-lg focus:outline-none focus:ring-4 resize-none shadow-[0_0_10px_rgba(168,85,247,0.35)] dark:shadow-[0_0_10px_rgba(168,85,247,0.25)] min-h-96 bg-white dark:bg-gray-950 text-gray-900 dark:text-white transition-colors duration-200`}
                placeholder="Enter your script here..."
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg relative border border-gray-200 dark:border-gray-700 transition-colors duration-200 rounded-t-2xl border-b-4 border-b-gray-300 dark:border-b-gray-600">
              {/* Tab buttons */}
              <div className="flex mb-4 -mt-4 -mx-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab("actors")}
                  className={`flex-1 py-3 font-semibold text-sm transition-colors duration-200 hover:cursor-pointer ${
                    activeTab === "actors"
                      ? "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-b-2 border-blue-400 dark:border-blue-600 rounded-tl-2xl"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  Actors
                </button>
              </div>

              {activeTab === "actors" ? (
                <>
                  {/* {itemType === "product" && !checkAllActorsHaveMergedImages() && (
                    <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center mb-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Product Image Required
                        </h3>
                      </div>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 ml-7">
                        Some selected actors need to have product images applied. Click the "Next" button below to continue.
                      </p>
                    </div>
                  )} */}
                  <div className="flex flex-wrap gap-4">
                    {selectedActors.map((actor) => (
                      <div key={actor.id} className="relative group w-32">
                        <img
                          src={transformImageUrl(
                            actorsWithMergedImages[actor.id] === true &&
                              mergedImageUrls[actor.id]
                              ? mergedImageUrls[actor.id]
                              : actor.preview_image_url || actor.image
                          )}
                          alt={actor.name}
                          className="w-full h-auto rounded-lg border-2 border-transparent hover:border-purple-300 dark:hover:border-purple-700 transition-all duration-200"
                        />
                        {/* Status indicator for merged images */}
                        {itemType === "product" && (
                          <div
                            className={`absolute top-2 right-2 h-4 w-4 rounded-full z-10 ${
                              actorsWithMergedImages[actor.id] === true
                                ? "bg-green-500"
                                : actorsWithMergedImages[actor.id] === false
                                ? "bg-red-500"
                                : "bg-gray-300"
                            }`}
                            title={
                              actorsWithMergedImages[actor.id] === true
                                ? "Character has a merged image"
                                : "Character needs a merged image"
                            }
                          />
                        )}
                        <button
                          onClick={() => removeActor(actor.id)}
                          className="absolute -top-2 -right-2 bg-white dark:bg-gray-700 rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-50 dark:hover:bg-red-900/20 hover:cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-red-500 dark:text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        {itemType === "product" && (
                          <button
                            onClick={() => {
                              // Store original actors before modifying
                              setOriginalSelectedActors([...selectedActors]);
                              // Set selectedActors to only this actor for editing
                              const actorToEdit = [actor];
                              setSelectedActors(actorToEdit);
                              setIsCharacterCanvasDialogOpen(true);
                            }}
                            className="absolute bottom-3.5 left-1/2 transform -translate-x-1/2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 py-1.5 px-3 text-white text-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full shadow-md hover:cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Edit
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="flex flex-col items-center justify-center bg-blue-50 dark:bg-blue-900/20 w-32 h-55 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/30 border-blue-200 dark:border-blue-800 border-2 rounded-lg transition-colors duration-200"
                      onClick={() => setIsActorDialogOpen(true)}
                    >
                      <PlusCircleIcon className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                      <span className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Add New Actor
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                    Video Instructions
                  </h3>
                </>
              )}
            </div>
          </div>

          {/* Add AudioSettings component */}
          {selectedActors.length > 0 && (
            <div className="mt-6">
              <AudioSettings
                selectedActors={selectedActors}
                projectId={projectId}
                onAddActor={() => setIsActorDialogOpen(true)}
              />
            </div>
          )}
        </div>
      )}

      {/* Add sticky action bar at the bottom of the page */}
      {hasLoadedMetadata && (
        <StickyBar
          selectedActors={selectedActors}
          scriptText={scriptText}
          handleGenerateVideo={handleGenerateVideo}
          handleNextForMergedImages={handleNextForMergedImages}
          isProcessing={isProcessing}
          user={user}
          isOverLimit={isOverLimit}
          allActorsHaveMergedImages={checkAllActorsHaveMergedImages()}
          itemType={itemType}
        />
      )}
      {/* Progress bar is no longer needed since we have the full-page overlay */}

      {/* Loading indicator while fetching metadata */}
      {!hasLoadedMetadata && <LoadingSpinner />}

      {/* Add a subtle indicator when metadata is being saved */}
      {isSavingMetadata && (
        <div className="fixed bottom-0 right-0 m-4 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-300 text-xs rounded-full shadow-sm">
          Saving...
        </div>
      )}
    </>
  );
}
